import { useCallback, useEffect, useRef } from 'react';
import { Dimensions, LayoutChangeEvent, Platform } from 'react-native';
import { useForceUpdate } from './useForceUpdate';
import { useIsFocused } from '@react-navigation/native';
import { batchMeasureViewRef } from '../utils/batchMeasureViewRef';
import { useRerenderOnInterval } from './useRerenderOnInterval';
import { useRegisterOnLayoutChangeListener } from '../components/v2BaseComponents/LayoutChangeEventProvider';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsKeyboardRestrictedView, useWindowHeightWithKeyboard } from '../components/shared/KeyboardRestrictedView';

/**
 * Helps normalize absolute positioning across platforms.
 *
 * There exists subtle inconsistencies in the way that absolute positioning is calculated across platforms.
 *
 * Validation can easily be done by viewing the AbsolutePositioningValidation component.
 */
export class ComputedPosition {
    private _rawPosition: RawPosition;
    private _insets: EdgeInsets;
    private _windowHeight: number;
    private _windowWidth: number;
    private _windowHeightWithKeyboard: number;
    private _isKeyboardVisible: boolean;
    constructor(
        rawPosition: RawPosition,
        insets: EdgeInsets,
        isKeyboardVisible: boolean,
        windowHeightWithKeyboard: number,
    ) {
        this._rawPosition = rawPosition;
        this._insets = insets;
        this._windowHeight = isKeyboardVisible ? windowHeightWithKeyboard : Dimensions.get('window').height;
        this._windowWidth = Dimensions.get('window').width;
        this._windowHeightWithKeyboard = windowHeightWithKeyboard;
        this._isKeyboardVisible = isKeyboardVisible;
    }

    public getHasRawPositionChangedEnoughForUpdate(
        rawPosition: RawPosition,
        windowHeightWithKeyboard: number,
        isKeyboardVisible: boolean,
    ) {
        const isEnoughDelta = (val1: number, val2: number) => Math.abs(val1 - val2) > 3;

        return (
            isEnoughDelta(this._rawPosition.x, rawPosition.x) ||
            isEnoughDelta(this._rawPosition.y, rawPosition.y) ||
            isEnoughDelta(this._rawPosition.width, rawPosition.width) ||
            isEnoughDelta(this._rawPosition.height, rawPosition.height) ||
            isEnoughDelta(this._rawPosition.pageX, rawPosition.pageX) ||
            isEnoughDelta(this._rawPosition.pageY, rawPosition.pageY) ||
            isEnoughDelta(this._windowHeightWithKeyboard, windowHeightWithKeyboard) ||
            this._isKeyboardVisible !== isKeyboardVisible
        );
    }

    /**
     * Top of the screen -> top of the element
     *
     * Handles observed inconsistencies in the top value on iOS
     */
    public get top(): number {
        return this._rawPosition.pageY - (Platform.OS === 'ios' || Platform.OS === 'android' ? this._insets.top : 0);
    }

    /**
     * Left of the screen -> left of the element
     */
    public get left(): number {
        return this._rawPosition.pageX - this._insets.left;
    }

    /**
     * Right of the screen -> right of the element
     */
    public get right(): number {
        return this._windowWidth - this._rawPosition.pageX - this._rawPosition.width - this._insets.right;
    }

    /**
     * Bottom of the screen -> bottom of the element
     */
    public get bottom(): number {
        // pageY (from ref.measure) is relative to the full physical window top (behind the status bar
        // under edge-to-edge). The portal host these values are applied within sits inside SafeAreaView,
        // whose `additive` insets shift its origin down by insets.top and shrink its bottom by insets.bottom.
        // A `bottom` anchor is the gap from the portal's bottom edge up to the element's bottom, which is
        // windowHeight - pageY - height - insets.bottom. No insets.top term belongs here (window-relative
        // pageY already spans the status-bar region); the previous Android-only `+ insets.top` over-corrected,
        // pushing bottom-anchored content up by ~status-bar height. This now matches the (correct) iOS path.
        return this._windowHeight - this._rawPosition.pageY - this._rawPosition.height - this._insets.bottom;
    }

    /**
     * Height of the element
     */
    public get height(): number {
        return this._rawPosition.height;
    }

    /**
     * Width of the element
     */
    public get width(): number {
        return this._rawPosition.width;
    }

    /**
     * The bottom position value for the top of the element.
     *
     * Example: A select popover that should be above the input
     */
    public get bottomAlignFromTop(): number {
        // Same coordinate-system reasoning as `bottom`: the Android-only `+ insets.top` over-corrected
        // and pushed above-the-element popovers up by ~status-bar height. Universal formula matches iOS.
        return this._windowHeight - this._rawPosition.pageY - this._insets.bottom;
    }

    /**
     * The top position value for the bottom of the element.
     *
     * Example: A select popover that should be below the input
     */
    public get topAlignFromBottom(): number {
        return this.top + this._rawPosition.height;
    }

    /**
     * The bottom position value for the top of the element with padding.
     *
     * Example: A select popover that should be above the input with padding
     */
    public getBottomAlignFromTopWithPadding(padding: number): number {
        return this.bottomAlignFromTop + padding;
    }

    /**
     * The top position value for the bottom of the element with padding.
     *
     * Example: A select popover that should be below the input with padding
     */
    public getTopAlignFromBottomWithPadding(padding: number): number {
        return this.topAlignFromBottom + padding;
    }

    /**
     * The left value of the horizontal midpoint of the element
     */
    public get viewMidpointX(): number {
        return this.left + this.width / 2;
    }

    /**
     * The top value of the vertical midpoint of the element
     */
    public get viewMidpointY(): number {
        return this.top + this.height / 2;
    }

    /**
     * Whether the element is on the top half of the screen
     */
    public get isOnTopHalfOfScreen(): boolean {
        return this.viewMidpointY < this._windowHeight / 2;
    }

    /**
     * The maximum position below the element.  Useful for determining the maximum height of a select popover
     * below the input
     */
    public getMaxVisibleHeightFromBottom(cutoffHeight = Infinity): number {
        return Math.min(this.bottom, cutoffHeight);
    }

    /**
     * The maximum position above the element.  Useful for determining the maximum height of a select popover
     * above the input
     */
    public getMaxVisibleHeightFromTop(cutoffHeight = Infinity): number {
        return Math.min(this.top, cutoffHeight);
    }

    /**
     * Determines if the element is completely within the viewport
     */
    public get isInViewport(): boolean {
        const viewRect = {
            rectTop: this._rawPosition.pageY,
            rectBottom: this._rawPosition.pageY,
            rectWidth: this._rawPosition.pageX + this._rawPosition.width,
        };

        return (
            viewRect.rectBottom != 0 &&
            viewRect.rectTop >= 0 &&
            viewRect.rectBottom <= this._windowHeight &&
            viewRect.rectWidth > 0 &&
            viewRect.rectWidth <= this._windowWidth
        );
    }
}

type RawPosition = {
    x: number;
    y: number;
    width: number;
    height: number;
    pageX: number;
    pageY: number;
};

export type UseElementPositionReturnValue = {
    ref: any;
    position: ComputedPosition | null;
    onLayout: (e: LayoutChangeEvent) => void;
};

export type UseElementPositionArgs = {
    isDisabled?: boolean;
    clearPositionOnDisable?: boolean;
};

/**
 * Hook to get the position of an element
 *
 * It's probably best to leverage onLayout for this, but that does not account for the position of the element on the screen changing
 */
export const useElementPosition = ({
    isDisabled,
    clearPositionOnDisable,
}: UseElementPositionArgs = {}): UseElementPositionReturnValue => {
    const windowHeightWithKeyboard = useWindowHeightWithKeyboard();
    const windowHeightWithKeyboardRef = useRef(windowHeightWithKeyboard);
    useEffect(() => {
        windowHeightWithKeyboardRef.current = windowHeightWithKeyboard;
    });
    const isKeyboardVisible = useIsKeyboardRestrictedView();
    const isKeyboardVisibleRef = useRef(isKeyboardVisible);
    useEffect(() => {
        isKeyboardVisibleRef.current = isKeyboardVisible;
    });
    const isFocused = useIsFocused();
    const { forceUpdate } = useForceUpdate();
    const ref = useRef<any>(null);
    const insets = useSafeAreaInsets();

    const isDisabledOrLostFocus = isDisabled || !isFocused;

    const positionRef = useRef<ComputedPosition | null>(null);

    if (isDisabledOrLostFocus && clearPositionOnDisable) {
        positionRef.current = null;
    }

    // rerender every second if ref not yet registered
    const shouldRerenderOnInterval = isFocused && !ref;
    useRerenderOnInterval({
        isDisabled: !shouldRerenderOnInterval,
        interval: 1000,
    });

    const measureLayout = useCallback(() => {
        if (isDisabledOrLostFocus) return;

        if (!ref.current) return;

        batchMeasureViewRef(
            ref,
            (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
                if (isDisabledOrLostFocus) return;

                const isValidMeasurement = [x, y, width, height, pageX, pageY].every((_) => _ !== undefined);

                if (!isValidMeasurement) return;

                const newPosition = { x, y, width, height, pageX, pageY };
                const shouldUpdate =
                    !positionRef.current ||
                    positionRef.current.getHasRawPositionChangedEnoughForUpdate(
                        newPosition,
                        windowHeightWithKeyboardRef.current,
                        isKeyboardVisibleRef.current,
                    );

                if (shouldUpdate) {
                    positionRef.current = new ComputedPosition(
                        newPosition,
                        insets,
                        isKeyboardVisibleRef.current,
                        windowHeightWithKeyboardRef.current,
                    );
                    forceUpdate();
                }
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceUpdate, isDisabledOrLostFocus]);

    useRegisterOnLayoutChangeListener(measureLayout);

    useEffect(() => {
        if (!isDisabledOrLostFocus) {
            measureLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDisabledOrLostFocus]);

    return {
        ref,
        position: positionRef.current,
        onLayout: measureLayout,
    };
};
